import { useState } from 'react';
import round from '../../../helpers/round';
import { Alert, Typography, Box, IconButton, Collapse, Tooltip, useMediaQuery } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from '@mui/material/Link';
import VerifiedIcon from '@mui/icons-material/Verified';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';

const MeyerhofResult = ({ result }) => {
  const { cohesion, phi, df, fndWidth, fndLength, theta, gamma, gwt, FsSt, FsSs, FSt, FSs } = result.inputs;
  const {
    canUserSeeCalculationSteps,
    nq,
    nc,
    isPhiZero,
    phiCondition,
    isThetaZero,
    thetaCondition,
    thetaConditionForIGamma,
    ngamma,
    kp,
    sc,
    sq,
    sgamma,
    dc,
    dq,
    dgamma,
    ic,
    iq,
    igamma,
    appliedCase,
    h,
    dw,
    effectiveStress,
    gammaEffective,
    gammaEffectivePrime,
    qUlt,
    qallSt,
    qallSs,
    isQallStEnough,
    isQallSsEnough,
  } = result.outputs;

  const isSmallScreen = useMediaQuery('(max-width:700px)');

  const MeyerhofInfoBox = () => {
    return (
      <Box sx={{ my: 1, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} gutterBottom>
            Meyerhof’s Bearing Capacity (1963)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            Meyerhof (1963) extended Terzaghi’s classical bearing capacity theory by introducing correction factors for footing
            shape, embedment depth, and load inclination. The method provides two formulations — one for
            <strong> vertical loading</strong> and another for <strong> inclined loading</strong>. Each term represents the
            contribution of soil cohesion (<i>c</i>), surcharge (<i>q̄</i>), and unit weight (<i>γ</i>) beneath the foundation,
            scaled by bearing capacity factors (
            <i>
              N<sub>c</sub>
            </i>
            ,{' '}
            <i>
              N<sub>q</sub>
            </i>
            ,{' '}
            <i>
              N<sub>γ</sub>
            </i>
            ) and the corresponding modification factors for shape (<i>s</i>), depth (<i>d</i>), and inclination (<i>i</i>).
          </Typography>

          {/* Vertical load */}
          <Typography color="text.secondary" sx={{ mt: 1.5, mb: 1, lineHeight: 1.6, fontWeight: 600 }}>
            <math display="block">
              <mrow>
                <mtext>Vertical load:</mtext>
                <mspace width="0.5em" />
                <msub>
                  <mi>q</mi>
                  <mi>ult</mi>
                </msub>
                <mo>=</mo>
                <mi>c</mi>
                <msub>
                  <mi>N</mi>
                  <mi>c</mi>
                </msub>
                <msub>
                  <mi>s</mi>
                  <mi>c</mi>
                </msub>
                <msub>
                  <mi>d</mi>
                  <mi>c</mi>
                </msub>
                <mo>+</mo>
                <mover>
                  <mi>q</mi>
                  <mo>¯</mo>
                </mover>
                <msub>
                  <mi>N</mi>
                  <mi>q</mi>
                </msub>
                <msub>
                  <mi>s</mi>
                  <mi>q</mi>
                </msub>
                <msub>
                  <mi>d</mi>
                  <mi>q</mi>
                </msub>
                <mo>+</mo>
                <mn>0.5</mn>
                <msub>
                  <mi>γ</mi>
                  <mo>e</mo>
                </msub>
                <mi>B</mi>
                <msub>
                  <mi>N</mi>
                  <mi>γ</mi>
                </msub>
                <msub>
                  <mi>s</mi>
                  <mi>γ</mi>
                </msub>
                <msub>
                  <mi>d</mi>
                  <mi>γ</mi>
                </msub>
              </mrow>
            </math>
          </Typography>

          {/* Inclined load */}
          <Typography color="text.secondary" sx={{ mt: 0.5, mb: 1.5, lineHeight: 1.6, fontWeight: 600 }}>
            <math display="block">
              <mrow>
                <mtext>Inclined load:</mtext>
                <mspace width="0.5em" />
                <msub>
                  <mi>q</mi>
                  <mi>ult</mi>
                </msub>
                <mo>=</mo>
                <mi>c</mi>
                <msub>
                  <mi>N</mi>
                  <mi>c</mi>
                </msub>
                <msub>
                  <mi>d</mi>
                  <mi>c</mi>
                </msub>
                <msub>
                  <mi>i</mi>
                  <mi>c</mi>
                </msub>
                <mo>+</mo>
                <mover>
                  <mi>q</mi>
                  <mo>¯</mo>
                </mover>
                <msub>
                  <mi>N</mi>
                  <mi>q</mi>
                </msub>
                <msub>
                  <mi>d</mi>
                  <mi>q</mi>
                </msub>
                <msub>
                  <mi>i</mi>
                  <mi>q</mi>
                </msub>
                <mo>+</mo>
                <mn>0.5</mn>
                <msub>
                  <mi>γ</mi>
                  <mo>e</mo>
                </msub>
                <mi>B</mi>
                <msub>
                  <mi>N</mi>
                  <mi>γ</mi>
                </msub>
                <msub>
                  <mi>d</mi>
                  <mi>γ</mi>
                </msub>
                <msub>
                  <mi>i</mi>
                  <mi>γ</mi>
                </msub>
              </mrow>
            </math>
          </Typography>
        </Alert>
      </Box>
    );
  };

  const BearingCapacityFactorsInfoBox = () => {
    const [showNcDetails, setShowNcDetails] = useState(false);
    const [showNqDetails, setShowNqDetails] = useState(false);
    const [showNgammaDetails, setShowNgammaDetails] = useState(false);

    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Bearing Capacity Factors
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
            In Meyerhof’s (1963) formulation, the bearing capacity factors (
            <i>
              N<sub>c</sub>, N<sub>q</sub>, N<sub>γ</sub>
            </i>
            ) quantify the influence of the soil’s internal friction angle (<i>φ</i>) on the ultimate bearing capacity. They
            represent the relative contribution of cohesion, surcharge, and the soil’s unit weight to the overall resistance of
            the foundation.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* surcharge factor - Nq */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowNqDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showNqDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>
                  N<sub>q</sub>
                </b>{' '}
                (surcharge factor): {nq || nq === 0 ? round(nq, 2) : '—'}
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showNqDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    We can use the following formula to calculate the surcharge factor N<sub>q</sub>:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>N</mi>
                        <mi>q</mi>
                      </msub>
                      <mo>=</mo>
                      <msup>
                        <mi>e</mi>
                        <mrow>
                          <mi>π</mi>

                          <mo>tan</mo>
                          <mo>(</mo>
                          <mn>&phi;</mn>
                          <mo>)</mo>
                        </mrow>
                      </msup>
                      <msup>
                        <mo>tan</mo>
                        <mn>2</mn>
                      </msup>
                      <mrow>
                        <mo>(</mo>
                        <mn>45</mn>
                        <mo>+</mo>
                        <mfrac>
                          <mn>&phi;</mn>

                          <mn>2</mn>
                        </mfrac>
                        <mo>)</mo>
                      </mrow>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    By substituting the input value of φ into the equation, we get:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>N</mi>
                        <mi>q</mi>
                      </msub>
                      <mo>=</mo>
                      <msup>
                        <mi>e</mi>
                        <mrow>
                          <mi>π</mi>

                          <mo>tan</mo>
                          <mo>(</mo>
                          <mn>{phi || phi === 0 ? round(phi, 2) : '—'}</mn>
                          <mo>)</mo>
                        </mrow>
                      </msup>
                      <msup>
                        <mo>tan</mo>
                        <mn>2</mn>
                      </msup>
                      <mrow>
                        <mo>(</mo>
                        <mn>45</mn>
                        <mo>+</mo>
                        <mfrac>
                          <mn>{phi || phi === 0 ? round(phi, 2) : '—'}</mn>
                          <mn>2</mn>
                        </mfrac>
                        <mo>)</mo>
                      </mrow>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    And from here;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>N</mi>
                        <mi>q</mi>
                      </msub>
                      <mtext> = </mtext>
                      <mn>{nq || nq === 0 ? round(nq, 2) : '—'}</mn>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* cohesion factor - Nc */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowNcDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showNcDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  N<sub>c</sub>
                </b>{' '}
                (cohesion factor): {nc || nc === 0 ? round(nc, 2) : '—'}
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showNcDetails} unmountOnExit>
                  {phi === 0 ? (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Since φ = 0<sup>o</sup>, the following formula should be used to calculate the cohesion factor N
                        <sub>c:</sub>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>N</mi>
                            <mi>c</mi>
                          </msub>
                          <mo> = </mo>
                          <mi>π</mi>
                          <mo> + </mo>
                          <mn>2</mn>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        And from here;
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>N</mi>
                            <mi>c</mi>
                          </msub>
                          <mtext> = </mtext>
                          <mn>{nc || nc === 0 ? round(nc, 2) : '—'}</mn>
                        </math>
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        We have already calculated that the surcharge factor, N<sub>q</sub>= {nq || nq === 0 ? round(nq, 2) : '—'}
                        . After that, the following formula can be used to calculate the cohesion factor N<sub>c:</sub>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math xmlns="http://www.w3.org/1998/Math/MathML" display="block">
                          <msub>
                            <mi>N</mi>
                            <mi>c</mi>
                          </msub>
                          <mo>=</mo>
                          <mrow>
                            <mo>(</mo>
                            <msub>
                              <mi>N</mi>
                              <mi>q</mi>
                            </msub>
                            <mo>−</mo>
                            <mn>1</mn>
                            <mo>)</mo>
                          </mrow>
                          <mo>cot</mo>
                          <mi>φ</mi>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        When we input also the input value φ, we will have this equation:
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>N</mi>
                            <mi>c</mi>
                          </msub>
                          <mtext> = </mtext>
                          <mrow>
                            <mo>(</mo>
                            <mn>{nq || nq === 0 ? round(nq, 2) : '—'}</mn>
                            <mo>−</mo>
                            <mn>1</mn>
                            <mo>)</mo>
                            <mo>×</mo>
                            <mo>cot(</mo>
                            <mn>{phi || phi === 0 ? round(phi, 2) : '—'}</mn>
                            <mo>)</mo>
                          </mrow>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        And from here;
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>N</mi>
                            <mi>c</mi>
                          </msub>
                          <mtext> = </mtext>
                          <mn>{nc || nc === 0 ? round(nc, 2) : '—'}</mn>
                        </math>
                      </Typography>
                    </>
                  )}
                </Collapse>
              )}
            </Alert>
            {/* unit weight factor - Ngamma */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowNgammaDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showNgammaDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  N<sub>&gamma;</sub>
                </b>{' '}
                (unit weight factor): {ngamma || ngamma === 0 ? round(ngamma, 2) : '—'}
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showNgammaDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    We have already calculated that the surcharge factor, N<sub>q</sub>= {nq || nq === 0 ? round(nq, 2) : '—'}.
                    After that, the following formula can be used to calculate the unit weight factor N<sub>&gamma;:</sub>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math xmlns="http://www.w3.org/1998/Math/MathML" display="block">
                      <msub>
                        <mi>N</mi>
                        <mi>γ</mi>
                      </msub>
                      <mo>=</mo>
                      <mrow>
                        <mo>(</mo>
                        <msub>
                          <mi>N</mi>
                          <mi>q</mi>
                        </msub>
                        <mo>−</mo>
                        <mn>1</mn>
                        <mo>)</mo>
                      </mrow>
                      <mo>·</mo>
                      <mo>tan</mo>
                      <mo>(</mo>
                      <mn>1.4</mn>
                      <mi>φ</mi>
                      <mo>)</mo>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    When we input also the input value φ, we will have this equation:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math xmlns="http://www.w3.org/1998/Math/MathML" display="block">
                      <msub>
                        <mi>N</mi>
                        <mi>γ</mi>
                      </msub>
                      <mo>=</mo>
                      <mrow>
                        <mo>(</mo>
                        <mi> {nq || nq === 0 ? round(nq, 2) : '—'}</mi>
                        <mo>−</mo>
                        <mn>1</mn>
                        <mo>)</mo>
                      </mrow>
                      <mo>·</mo>
                      <mo>tan</mo>
                      <mo>(</mo>
                      <mn>1.4</mn>
                      <mn>&times;</mn>
                      <mi>{phi || phi === 0 ? round(phi, 2) : '—'}</mi>
                      <mo>)</mo>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    And from here;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>N</mi>
                        <mi>&gamma;</mi>
                      </msub>
                      <mtext> = </mtext>
                      <mi> {ngamma || ngamma === 0 ? round(ngamma, 2) : '—'}</mi>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };

  const ShapeFactorsInfoBox = () => {
    const [showKpDetails, setShowKpDetails] = useState(false);
    const [showScDetails, setShowScDetails] = useState(false);
    const [showSqSGammaDetails, setShowSqSGammaDetails] = useState(false);

    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Shape Factors
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
            In Meyerhof’s formulation, the shape factors (
            <i>
              s<sub>c</sub>
            </i>
            ,{' '}
            <i>
              s<sub>q</sub>
            </i>
            ,{' '}
            <i>
              s<sub>γ</sub>
            </i>
            ) refine the bearing capacity equation to incorporate the effects of footing geometry. These factors modify the
            influence of cohesion, surcharge, and unit weight components for different foundation shapes.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Coefficient of passive earth pressure - Kp*/}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowKpDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showKpDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  K<sub>p</sub>
                </b>{' '}
                (Coefficient of passive earth pressure): {kp || kp === 0 ? round(kp, 2) : '—'}
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showKpDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    To calculate the Meyerhof shape factors (
                    <i>
                      s<sub>c</sub>
                    </i>
                    ,{' '}
                    <i>
                      s<sub>q</sub>
                    </i>
                    ,{' '}
                    <i>
                      s<sub>γ</sub>
                    </i>
                    ), we first need to determine the coefficient of passive earth pressure (
                    <i>
                      K<sub>p</sub>
                    </i>
                    ), which depends on the soil friction angle (<i>φ</i>) and is given by;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>K</mi>
                        <mi>p</mi>
                      </msub>
                      <mo>=</mo>
                      <msup>
                        <mi>tan</mi>
                        <mn>2</mn>
                      </msup>
                      <mo>(</mo>
                      <mn>45</mn>
                      <mo>°</mo>
                      <mo>+</mo>
                      <mfrac>
                        <mi>φ</mi>
                        <mn>2</mn>
                      </mfrac>
                      <mo>)</mo>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    Substituting the input value of φ into the equation, we get:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>K</mi>
                        <mi>p</mi>
                      </msub>
                      <mo>=</mo>
                      <msup>
                        <mi>tan</mi>
                        <mn>2</mn>
                      </msup>
                      <mo>(</mo>
                      <mn>45</mn>
                      <mo>°</mo>
                      <mo>+</mo>
                      <mfrac>
                        <mi>{phi || phi === 0 ? round(phi, 2) : '—'}</mi>
                        <mn>2</mn>
                      </mfrac>
                      <mo>)</mo>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    So we have;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>K</mi>
                        <mi>p</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>{kp || kp === 0 ? round(kp, 2) : '—'}</mn>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* cohesion shape factor - sc*/}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowScDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showScDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  s<sub>c</sub>
                </b>{' '}
                (Cohesion shape factor): {sc || sc === 0 ? round(sc, 2) : '—'}
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showScDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    We can use the following expression to calculate the cohesion shape factor (s<sub>c</sub>) according to
                    Meyerhof (1963):
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>s</mi>
                        <mi>c</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>1</mn>
                      <mo>+</mo>
                      <mn>0.2</mn>
                      <mo>×</mo>
                      <msub>
                        <mi>K</mi>
                        <mi>p</mi>
                      </msub>
                      <mo>×</mo>
                      <mfrac>
                        <mi>B</mi>
                        <mi>L</mi>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    Since we have already calculated the coefficient of passive earth pressure (
                    <i>
                      K<sub>p</sub>{' '}
                    </i>
                    ), we can substitute its value along with the foundation width (B) and length (L) into the equation:
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>s</mi>
                        <mi>c</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>1</mn>
                      <mo>+</mo>
                      <mn>0.2</mn>
                      <mo>×</mo>
                      <mi>{kp || kp === 0 ? round(kp, 2) : '—'}</mi>
                      <mo>×</mo>
                      <mfrac>
                        <mi>{fndWidth || fndWidth === 0 ? round(fndWidth, 2) : '—'}</mi>
                        <mi>{fndLength || fndLength === 0 ? round(fndLength, 2) : '—'}</mi>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    And from here;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>s</mi>
                        <mi>c</mi>
                      </msub>
                      <mo>=</mo>
                      <mi>{sc || sc === 0 ? round(sc, 2) : '—'}</mi>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/*  sq and sGamma*/}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowSqSGammaDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showSqSGammaDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  s<sub>q</sub>
                </b>{' '}
                (Surcharge shape factor) ={' '}
                <b>
                  s<sub>&gamma;</sub>
                </b>{' '}
                (Unit weight shape factor): {sq || sq === 0 ? round(sq, 2) : '—'}
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showSqSGammaDetails} unmountOnExit>
                  {phiCondition && phiCondition === 1 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Since φ = 0°, the surcharge shape factor (s<sub>q</sub>) and unit weight shape factor (s<sub>&gamma;</sub>
                        ) are both equal to 1.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>s</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>s</mi>
                            <mi>&gamma;</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>1</mn>
                        </math>
                      </Typography>
                    </>
                  )}
                  {phiCondition && phiCondition === 2 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Since 0° &lt; &phi; &le; 10° the following linear interpolation should be used to calculate the surcharge
                        shape factor (s
                        <sub>q</sub>) and unit weight shape factor (s
                        <sub>&gamma;</sub>):
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>s</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>s</mi>
                            <mi>&gamma;</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>1</mn>
                          <mo>+</mo>
                          <mi>IR</mi>
                          <mo>×</mo>
                          <mn>0.1</mn>
                          <mo>×</mo>
                          <msub>
                            <mi>K</mi>
                            <mi>p</mi>
                          </msub>
                          <mo>×</mo>
                          <mfrac>
                            <mi>B</mi>
                            <mi>L</mi>
                          </mfrac>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Where;
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <mi>IR(Interpolation Ratio)</mi>
                          <mo>=</mo>
                          <mfrac>
                            <mi>&phi;</mi>
                            <mi>10</mi>
                          </mfrac>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Substituting the value of φ along with the previously calculated coefficient of passive earth pressure (K
                        <sub>p</sub>), foundation width (B), and length (L) into the equation:
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>s</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>s</mi>
                            <mi>&gamma;</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>1</mn>
                          <mo>+</mo>
                          <mfrac>
                            <mi>{phi || phi === 0 ? round(phi, 2) : '—'}</mi>
                            <mi>10</mi>
                          </mfrac>
                          <mo>×</mo>
                          <mn>0.1</mn>
                          <mo>×</mo>
                          <mi>{kp || kp === 0 ? round(kp, 2) : '—'}</mi>
                          <mo>×</mo>
                          <mfrac>
                            <mi>{fndWidth || fndWidth === 0 ? round(fndWidth, 2) : '—'}</mi>
                            <mi>{fndLength || fndLength === 0 ? round(fndLength, 2) : '—'}</mi>
                          </mfrac>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        So we have;
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>s</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>s</mi>
                            <mi>&gamma;</mi>
                          </msub>
                          <mo>=</mo>
                          <mi>{sq || sq === 0 ? round(sq, 2) : '—'}</mi>
                        </math>
                      </Typography>
                    </>
                  )}
                  {phiCondition && phiCondition === 3 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Since φ &gt; 10°, the following formula should be used to calculate the surcharge shape factor (s
                        <sub>q</sub>) and unit weight shape factor (s
                        <sub>&gamma;</sub>):
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>s</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>s</mi>
                            <mi>&gamma;</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>1</mn>
                          <mo>+</mo>

                          <mn>0.1</mn>
                          <mo>×</mo>
                          <msub>
                            <mi>K</mi>
                            <mi>p</mi>
                          </msub>
                          <mo>×</mo>
                          <mfrac>
                            <mi>B</mi>
                            <mi>L</mi>
                          </mfrac>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Substituting the previously calculated coefficient of passive earth pressure (K
                        <sub>p</sub>), foundation width (B), and length (L) into the equation:
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>s</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>s</mi>
                            <mi>&gamma;</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>1</mn>
                          <mo>+</mo>
                          <mn>0.1</mn>
                          <mo>×</mo>
                          <mi>{kp || kp === 0 ? round(kp, 2) : '—'}</mi>
                          <mo>×</mo>
                          <mfrac>
                            <mi>{fndWidth || fndWidth === 0 ? round(fndWidth, 2) : '—'}</mi>
                            <mi>{fndLength || fndLength === 0 ? round(fndLength, 2) : '—'}</mi>
                          </mfrac>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        So we have;
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>s</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>s</mi>
                            <mi>&gamma;</mi>
                          </msub>
                          <mo>=</mo>
                          <mi>{sq || sq === 0 ? round(sq, 2) : '—'}</mi>
                        </math>
                      </Typography>
                    </>
                  )}
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };

  const DepthFactorsInfoBox = () => {
    const [showKpDetails, setShowKpDetails] = useState(false);
    const [showDcDetails, setShowDcDetails] = useState(false);
    const [showDqDGammaDetails, setShowDqDGammaDetails] = useState(false);

    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Depth Factors
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
            In Meyerhof’s formulation, the depth factors (
            <i>
              d<sub>c</sub>
            </i>
            ,{' '}
            <i>
              d<sub>q</sub>
            </i>
            ,{' '}
            <i>
              d<sub>γ</sub>
            </i>
            ) adjust the bearing capacity components to account for the effect of foundation embedment. These factors increase the
            contribution of cohesion, surcharge, and unit weight terms as the depth of the foundation increases, reflecting the
            confining influence of the surrounding soil.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Coefficient of passive earth pressure - Kp*/}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowKpDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showKpDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  K<sub>p</sub>
                </b>{' '}
                (Coefficient of passive earth pressure): {kp || kp === 0 ? round(kp, 2) : '—'}
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showKpDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    To calculate the Meyerhof depth factors (
                    <i>
                      d<sub>c</sub>
                    </i>
                    ,{' '}
                    <i>
                      d<sub>q</sub>
                    </i>
                    ,{' '}
                    <i>
                      d<sub>γ</sub>
                    </i>
                    ), we first need to determine the coefficient of passive earth pressure (
                    <i>
                      K<sub>p</sub>
                    </i>
                    ), which depends on the soil friction angle (<i>φ</i>) and is given by;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>K</mi>
                        <mi>p</mi>
                      </msub>
                      <mo>=</mo>
                      <msup>
                        <mi>tan</mi>
                        <mn>2</mn>
                      </msup>
                      <mo>(</mo>
                      <mn>45</mn>
                      <mo>°</mo>
                      <mo>+</mo>
                      <mfrac>
                        <mi>φ</mi>
                        <mn>2</mn>
                      </mfrac>
                      <mo>)</mo>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    Substituting the input value of φ into the equation, we get:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>K</mi>
                        <mi>p</mi>
                      </msub>
                      <mo>=</mo>
                      <msup>
                        <mi>tan</mi>
                        <mn>2</mn>
                      </msup>
                      <mo>(</mo>
                      <mn>45</mn>
                      <mo>°</mo>
                      <mo>+</mo>
                      <mfrac>
                        <mi>{phi || phi === 0 ? round(phi, 2) : '—'}</mi>
                        <mn>2</mn>
                      </mfrac>
                      <mo>)</mo>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    So we have;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>K</mi>
                        <mi>p</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>{kp || kp === 0 ? round(kp, 2) : '—'}</mn>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* cohesion depth factor - dc*/}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowDcDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showDcDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  d<sub>c</sub>
                </b>{' '}
                (Cohesion depth factor): {dc || dc === 0 ? round(dc, 2) : '—'}
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showDcDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    We can use the following expression to calculate the cohesion depth factor (d<sub>c</sub>) according to
                    Meyerhof (1963):
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>d</mi>
                        <mi>c</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>1</mn>
                      <mo>+</mo>
                      <mn>0.2</mn>

                      <msub>
                        <msqrt>
                          <mi>K</mi>
                          <mi>p</mi>
                        </msqrt>
                      </msub>

                      <mo>×</mo>
                      <mfrac>
                        <msub>
                          <mi>D</mi>
                          <mi>f</mi>
                        </msub>
                        <mi>B</mi>
                      </mfrac>
                    </math>
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    Since we have already calculated the coefficient of passive earth pressure (
                    <i>
                      K<sub>p</sub>{' '}
                    </i>
                    ), we can substitute its value along with the foundation width (B) and depth (D<sub>f</sub>) into the
                    equation:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>d</mi>
                        <mi>c</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>1</mn>
                      <mo>+</mo>
                      <mn>0.2</mn>

                      <msub>
                        <msqrt>
                          <mi>{kp || kp === 0 ? round(kp, 2) : '—'}</mi>
                        </msqrt>
                      </msub>

                      <mo>×</mo>
                      <mfrac>
                        <mi>{df || df === 0 ? round(df, 2) : '—'}</mi>
                        <mi>
                          <mi>{fndWidth || fndWidth === 0 ? round(fndWidth, 2) : '—'}</mi>
                        </mi>
                      </mfrac>
                    </math>
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    And from here;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>d</mi>
                        <mi>c</mi>
                      </msub>
                      <mo>=</mo>
                      <mi>{dc || dc === 0 ? round(dc, 2) : '—'}</mi>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/*  dq and dGamma*/}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowDqDGammaDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showDqDGammaDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  d<sub>q</sub>
                </b>{' '}
                (Surcharge depth factor) ={' '}
                <b>
                  d<sub>&gamma;</sub>
                </b>{' '}
                (Unit weight depth factor): {dq || dq === 0 ? round(dq, 2) : '—'}
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showDqDGammaDetails} unmountOnExit>
                  {phiCondition && phiCondition === 1 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Since φ = 0°, the surcharge depth factor (d<sub>q</sub>) and unit weight depth factor (d<sub>&gamma;</sub>
                        ) are both equal to 1.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>d</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>d</mi>
                            <mi>&gamma;</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>1</mn>
                        </math>
                      </Typography>
                    </>
                  )}
                  {phiCondition && phiCondition === 2 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Since 0° &lt; &phi; &le; 10° the following linear interpolation should be used to calculate the surcharge
                        depth factor (d
                        <sub>q</sub>) and unit weight depth factor (d
                        <sub>&gamma;</sub>):
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>d</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>d</mi>
                            <mi>&gamma;</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>1</mn>
                          <mo>+</mo>
                          <mi>IR</mi>
                          <mo>×</mo>
                          <mn>0.1</mn>
                          <mo>×</mo>
                          <msub>
                            <msqrt>
                              <mi>K</mi>
                              <mi>p</mi>
                            </msqrt>
                          </msub>
                          <mo>×</mo>
                          <mfrac>
                            <msub>
                              <mi>D</mi>
                              <mi>f</mi>
                            </msub>
                            <mi>B</mi>
                          </mfrac>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Where;
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <mi>IR(Interpolation Ratio)</mi>
                          <mo>=</mo>
                          <mfrac>
                            <mi>&phi;</mi>
                            <mi>10</mi>
                          </mfrac>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Substituting the value of φ along with the previously calculated coefficient of passive earth pressure (K
                        <sub>p</sub>), foundation width (B), and depth (D<sub>f</sub>) into the equation:
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>d</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>d</mi>
                            <mi>&gamma;</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>1</mn>
                          <mo>+</mo>
                          <mfrac>
                            <mi>{phi || phi === 0 ? round(phi, 2) : '—'}</mi>
                            <mi>10</mi>
                          </mfrac>
                          <mo>×</mo>
                          <mn>0.1</mn>
                          <mo>×</mo>
                          <msqrt>
                            <mi>{kp || kp === 0 ? round(kp, 2) : '—'}</mi>
                          </msqrt>
                          <mo>×</mo>
                          <mfrac>
                            <mi>{df || df === 0 ? round(df, 2) : '—'}</mi>
                            <mi>{fndWidth || fndWidth === 0 ? round(fndWidth, 2) : '—'}</mi>
                          </mfrac>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        So we have;
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>d</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>d</mi>
                            <mi>&gamma;</mi>
                          </msub>
                          <mo>=</mo>
                          <mi>{dq || dq === 0 ? round(dq, 2) : '—'}</mi>
                        </math>
                      </Typography>
                    </>
                  )}
                  {phiCondition && phiCondition === 3 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Since φ &gt; 10°, the following formula should be used to calculate the surcharge depth factor (d
                        <sub>q</sub>) and unit weight depth factor (d
                        <sub>&gamma;</sub>):
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>d</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>d</mi>
                            <mi>&gamma;</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>1</mn>
                          <mo>+</mo>
                          <mn>0.1</mn>
                          <mo>×</mo>
                          <msub>
                            <msqrt>
                              <mi>K</mi>
                              <mi>p</mi>
                            </msqrt>
                          </msub>
                          <mo>×</mo>
                          <mfrac>
                            <msub>
                              <mi>D</mi>
                              <mi>f</mi>
                            </msub>
                            <mi>B</mi>
                          </mfrac>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Substituting the previously calculated coefficient of passive earth pressure (K
                        <sub>p</sub>), foundation width (B), and depth (D<sub>f</sub>) into the equation:
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>d</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>d</mi>
                            <mi>&gamma;</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>1</mn>
                          <mo>+</mo>

                          <mn>0.1</mn>
                          <mo>×</mo>
                          <msqrt>
                            <mi>{kp || kp === 0 ? round(kp, 2) : '—'}</mi>
                          </msqrt>
                          <mo>×</mo>
                          <mfrac>
                            <mi>{df || df === 0 ? round(df, 2) : '—'}</mi>
                            <mi>{fndWidth || fndWidth === 0 ? round(fndWidth, 2) : '—'}</mi>
                          </mfrac>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        So we have;
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>d</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>d</mi>
                            <mi>&gamma;</mi>
                          </msub>
                          <mo>=</mo>
                          <mi>{dq || dq === 0 ? round(dq, 2) : '—'}</mi>
                        </math>
                      </Typography>
                    </>
                  )}
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };

  const InclinationFactorsInfoBox = () => {
    const [showIcIqDetails, setShowIcIqDetails] = useState(false);
    const [showIGammaDetails, setShowIGammaDetails] = useState(false);

    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Inclination Factors
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
            In Meyerhof’s formulation, the inclination factors (
            <i>
              i<sub>c</sub>
            </i>
            ,{' '}
            <i>
              i<sub>q</sub>
            </i>
            ,{' '}
            <i>
              i<sub>γ</sub>
            </i>
            ) modify the bearing capacity components to account for the effect of inclined loading on the foundation. These
            factors reduce the contributions of cohesion, surcharge, and unit weight terms as the load inclination increases,
            representing the loss of vertical effectiveness under oblique loading conditions.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* ic & iq*/}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowIcIqDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showIcIqDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  i<sub>c</sub>
                </b>{' '}
                (Cohesion inclination factor) ={' '}
                <b>
                  i<sub>q</sub>
                </b>{' '}
                (Surcharge inclination factor): {ic || ic === 0 ? round(ic, 2) : '—'}
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showIcIqDetails} unmountOnExit>
                  {thetaCondition && thetaCondition === 1 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Since &theta; = 0°, it means the load is vertical, so both the cohesion inclination factor (i<sub>c</sub>)
                        and surcharge inclination factor (i<sub>q</sub>) are equal to 1.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>i</mi>
                            <mi>c</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>i</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>1</mn>
                        </math>
                      </Typography>
                    </>
                  )}
                  {thetaCondition && thetaCondition === 2 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Since 0° &lt; &theta; &lt; 90° , we have inclined loading conditions and so the following equation should
                        be used to calculate the surcharge inclination factor (i<sub>q</sub>) and cohesion inclination factor (i
                        <sub>c</sub>):
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>i</mi>
                            <mi>c</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>i</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <msup>
                            <mrow>
                              <mo>(</mo>
                              <mn>1</mn>
                              <mo>−</mo>
                              <mfrac>
                                <mi>θ</mi>
                                <mn>90°</mn>
                              </mfrac>
                              <mo>)</mo>
                            </mrow>
                            <mn>2</mn>
                          </msup>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Substituting the value of &theta; into the equation:
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>i</mi>
                            <mi>c</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>i</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <msup>
                            <mrow>
                              <mo>(</mo>
                              <mn>1</mn>
                              <mo>−</mo>
                              <mfrac>
                                <mi>{theta || theta === 0 ? round(theta, 2) : '—'}°</mi>
                                <mn>90°</mn>
                              </mfrac>
                              <mo>)</mo>
                            </mrow>
                            <mn>2</mn>
                          </msup>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        So we have;
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>i</mi>
                            <mi>c</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>i</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <mi>{ic || ic === 0 ? round(ic, 2) : '—'}</mi>
                        </math>
                      </Typography>
                    </>
                  )}
                  {thetaCondition && thetaCondition === 3 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Since &theta; = 90°, it means the load is horizontal, so both the cohesion inclination factor (i
                        <sub>c</sub>) and surcharge inclination factor (i<sub>q</sub>) are equal to 0.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>i</mi>
                            <mi>c</mi>
                          </msub>
                          <mo>=</mo>
                          <msub>
                            <mi>i</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>0</mn>
                        </math>
                      </Typography>
                    </>
                  )}
                </Collapse>
              )}
            </Alert>
            {/* iGamma*/}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowIGammaDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showIGammaDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  i<sub>&gamma;</sub>
                </b>{' '}
                (Unit weight inclination factor): {igamma || igamma === 0 ? round(igamma, 2) : '—'}
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showIGammaDetails} unmountOnExit>
                  {thetaConditionForIGamma && thetaConditionForIGamma === 1 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        &theta; = 0° <span style={{ verticalAlign: '2px' }}>&rarr;</span> The load is perfectly vertical. There’s
                        no inclination effect, so the entire unit-weight component of bearing capacity remains active. (i
                        <sub>&gamma;</sub>) is equal to 1.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>i</mi>
                            <mi>&gamma;</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>1</mn>
                        </math>
                      </Typography>
                    </>
                  )}
                  {thetaConditionForIGamma && thetaConditionForIGamma === 2 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        &phi; = 0° and &theta; &gt; 0 <span style={{ verticalAlign: '2px' }}>&rarr;</span> The soil is purely
                        cohesive (no friction) and the load is inclined. Because clay cannot mobilize frictional resistance, the
                        unit-weight term is ignored, giving iγ = 0.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>i</mi>
                            <mi>&gamma;</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>0</mn>
                        </math>
                      </Typography>
                    </>
                  )}
                  {thetaConditionForIGamma && thetaConditionForIGamma === 3 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        &phi; &gt; 0° and &theta; = &phi; <span style={{ verticalAlign: '2px' }}>&rarr;</span> When the load
                        inclination angle (θ) equals the soil’s friction angle (φ), the footing is at the limit of sliding
                        stability. At this point, the soil cannot provide any additional resistance from its self-weight, so the
                        γ-term contribution becomes zero.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>i</mi>
                            <mi>&gamma;</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>0</mn>
                        </math>
                      </Typography>
                    </>
                  )}
                  {thetaConditionForIGamma && thetaConditionForIGamma === 4 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        &phi; &gt; 0° and &theta; &gt; &phi; <span style={{ verticalAlign: '2px' }}>&rarr;</span> The load
                        inclination angle (θ) is greater than the soil’s friction angle (φ). At this stage, the horizontal
                        component of the load exceeds the soil’s shear resistance, leading to potential sliding failure.
                        Therefore, the unit-weight contribution (γ-term) is considered ineffective and taken as zero.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>i</mi>
                            <mi>γ</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>0</mn>
                        </math>
                      </Typography>
                    </>
                  )}
                  {thetaConditionForIGamma && thetaConditionForIGamma === 5 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        &phi; &gt; 0° and 0° &lt; &theta; &lt; &phi; <span style={{ verticalAlign: '2px' }}>&rarr;</span> The soil
                        is frictional, and the load is moderately inclined. The inclination angle (θ) is smaller than the soil’s
                        friction angle (φ), so some of the self-weight resistance remains effective. The γ-term contribution
                        decreases with increasing θ.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>i</mi>
                            <mi>γ</mi>
                          </msub>
                          <mo>=</mo>
                          <msup>
                            <mrow>
                              <mo>(</mo>
                              <mn>1</mn>
                              <mo>−</mo>
                              <mfrac>
                                <mi>θ</mi>
                                <mi>φ</mi>
                              </mfrac>
                              <mo>)</mo>
                            </mrow>
                            <mn>2</mn>
                          </msup>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Substituting the values of &theta; and &phi; into the equation:
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>i</mi>
                            <mi>γ</mi>
                          </msub>
                          <mo>=</mo>
                          <msup>
                            <mrow>
                              <mo>(</mo>
                              <mn>1</mn>
                              <mo>−</mo>
                              <mfrac>
                                <mi>{theta || theta === 0 ? round(theta, 2) : '—'}</mi>
                                <mi>{phi || phi === 0 ? round(phi, 2) : '—'}</mi>
                              </mfrac>
                              <mo>)</mo>
                            </mrow>
                            <mn>2</mn>
                          </msup>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        We have;
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>i</mi>
                            <mi>γ</mi>
                          </msub>
                          <mo>=</mo>
                          <mi>{igamma || igamma === 0 ? round(igamma, 2) : '—'}</mi>
                        </math>
                      </Typography>
                    </>
                  )}
                  {thetaConditionForIGamma && thetaConditionForIGamma === 6 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Invalid or undefined input values <span style={{ verticalAlign: '2px' }}>&rarr;</span> The provided
                        friction angle (φ) or load inclination (θ) is outside the expected range. This condition acts as a
                        safeguard to prevent undefined or physically meaningless results in the calculation of the unit-weight
                        inclination factor.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>i</mi>
                            <mi>γ</mi>
                          </msub>
                          <mo>=</mo>
                          <mtext>—</mtext>
                        </math>
                      </Typography>
                    </>
                  )}
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };

  const WaterTableGammaEInfoBox = () => {
    const [open, setOpen] = useState(false);
    const gammaW = 9.806; // water unit weight, used as "gravity" in backend
    const gammaPrime = gamma - gammaW;

    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert
          icon={isSmallScreen ? false : <InfoOutlinedIcon />}
          severity="info"
          variant="outlined"
          sx={{
            p: 2,
          }}
        >
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
            Effect of Water Table — γ<sub>e</sub> Term
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
            To account for the water table on the unit-weight term of Meyerhof's equation, an average effective unit weight γ
            <sub>e</sub> is used over the failure wedge depth H. Your solver follows the Bowles-style wedge interpolation when the
            water table intersects the wedge.
          </Typography>

          <Alert
            icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
            severity="success"
            variant="outlined"
            sx={{
              backgroundColor: 'rgba(46,125,50,0.04)',
              color: 'inherit',
            }}
            action={
              canUserSeeCalculationSteps ? (
                <IconButton size="small" color="inherit" onClick={() => setOpen((v) => !v)}>
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.15s' }}
                  />
                </IconButton>
              ) : (
                <Tooltip
                  title={
                    <span>
                      Upgrade to unlock calculation details.{' '}
                      <Link
                        href="/pricing"
                        target="_blank"
                        rel="noopener"
                        style={{ color: '#90caf9', textDecoration: 'underline' }}
                      >
                        See plans
                      </Link>
                    </span>
                  }
                  leaveDelay={500}
                  arrow
                >
                  <LockOutlinedIcon
                    sx={{
                      fontSize: 20,
                      color: 'text.disabled',
                      cursor: 'not-allowed',
                      mr: 1,
                      mt: 0.5,
                    }}
                  />
                </Tooltip>
              )
            }
          >
            <Typography variant="body2" color="text.secondary">
              <b>
                γ<sub>e</sub>
              </b>{' '}
              (av. effective unit weight):&nbsp;{gammaEffective.toFixed(2)} kN/m³ , Case {appliedCase.caseId}:{appliedCase.note}
            </Typography>
            {/* Collapsible details */}
            {canUserSeeCalculationSteps && (
              <Collapse in={open} unmountOnExit>
                <Box sx={{ pl: 0.5, pr: 0.5 }} textAlign={'center'}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 0.5, mt: 1, lineHeight: 1.6, fontWeight: 600 }}
                  >
                    Piecewise rule:
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                    Case 1 - If GWT ≤ D<sub>f</sub>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    γ<sub>e</sub> = γ′ = γ − γ<sub>w</sub>
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                    Case 2 - If D<sub>f</sub> &lt; GWT ≤ H{' '}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    <math>
                      <msub>
                        <mi>γ</mi>
                        <mi>e</mi>
                      </msub>
                      <mo>=</mo>
                      <mrow>
                        <mi>γ</mi>
                        <mo>&#x2062;</mo>
                        <mo>[</mo>
                        <mfrac>
                          <mrow>
                            <mo>(</mo>
                            <mn>2</mn>
                            <mi>H</mi>
                            <mo>−</mo>
                            <msub>
                              <mi>d</mi>
                              <mi>w</mi>
                            </msub>
                            <mo>)</mo>
                            <msub>
                              <mi>d</mi>
                              <mi>w</mi>
                            </msub>
                          </mrow>
                          <msup>
                            <mi>H</mi>
                            <mn>2</mn>
                          </msup>
                        </mfrac>
                        <mo>]</mo>
                      </mrow>
                      <mo>+</mo>
                      <mrow>
                        <mi>γ</mi>
                        <mo>′</mo>
                        <mo>&#x2062;</mo>
                        <mo>[</mo>
                        <mfrac>
                          <msup>
                            <mrow>
                              <mo>(</mo>
                              <mi>H</mi>
                              <mo>−</mo>
                              <msub>
                                <mi>d</mi>
                                <mi>w</mi>
                              </msub>
                              <mo>)</mo>
                            </mrow>
                            <mn>2</mn>
                          </msup>
                          <msup>
                            <mi>H</mi>
                            <mn>2</mn>
                          </msup>
                        </mfrac>
                        <mo>]</mo>
                      </mrow>
                    </math>
                  </Typography>

                  <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                    Case 3 - If GWT &gt; H
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    γ<sub>e</sub> = γ
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.6 }}>
                    Where;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <math>
                      <mi>H</mi>
                      <mo>=</mo>
                      <msub>
                        <mi>D</mi>
                        <mi>f</mi>
                      </msub>
                      <mo>+</mo>
                      <mn>0.5</mn>
                      <mi>B</mi>
                      <mo>tan</mo>
                      <mo>(</mo>
                      <mn>45</mn>
                      <mo>°</mo>
                      <mo>+</mo>
                      <mi>φ</mi>
                      <mo>/</mo>
                      <mn>2</mn>
                      <mo>)</mo>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <math display="inline">
                      <msub>
                        <mi>d</mi>
                        <mi>w</mi>
                      </msub>
                      <mo>=</mo>
                      <mi>GWT</mi>
                      <mo>−</mo>
                      <msub>
                        <mi>D</mi>
                        <mi>f</mi>
                      </msub>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <math>
                      <mi>γ</mi>
                      <mo>′</mo>
                      <mo>=</mo>
                      <mi>γ</mi>
                      <mo>−</mo>
                      <msub>
                        <mi>γ</mi>
                        <mi>w</mi>
                      </msub>
                    </math>
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mt: 1 }}>
                    Input Parameters
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    D<sub>f</sub> = {df} m,&nbsp; B = {fndWidth} m,&nbsp; φ = {phi}°,&nbsp; GWT = {gwt} m ,&nbsp;γ = {gamma}
                    kN/m³,&nbsp;γ<sub>w</sub> = 9.806 kN/m³
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mt: 1 }}>
                    Calculated Parameters
                  </Typography>
                  {appliedCase.caseId === 1 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        It is known from the inputs that GWT = {gwt}m &le; D<sub>f</sub> = {df}m. So, use <b>Case 1</b>{' '}
                        formulation to calculate γ<sub>e</sub>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        γ<sub>e</sub> = γ′ = γ − γ<sub>w</sub> = {gamma.toFixed(2)} - {gammaW.toFixed(2)} ={' '}
                        {gammaPrime.toFixed(2)} kN/m³
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        γ<sub>e</sub> = {gammaPrime.toFixed(2)} kN/m³
                      </Typography>
                    </>
                  )}
                  {appliedCase.caseId === 2 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        It is known from the inputs that GWT = {gwt}m &gt; D<sub>f</sub> = {df}m. So, we should also check the
                        failure wedge depth (H) to find if the case is 2 or 3.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        H = D<sub>f</sub> + 0.5Btan(45 + &phi;/2)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        H = {df} + (0.5)({fndWidth})tan(45 + {phi}/2)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        H = {h.toFixed(2)}m
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        {' '}
                        Since GWT = {gwt}m &le; H = {h.toFixed(2)}m, use <b>Case 2</b> formulation to calculate γ<sub>e</sub>
                      </Typography>

                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2} className="math-scale">
                        <math display="inline">
                          <msub>
                            <mi>γ</mi>
                            <mi>e</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>{gamma.toFixed(2)}</mn>
                          <mo>[</mo>
                          <mfrac>
                            <mrow>
                              <mo>(</mo>
                              <mn>2</mn>
                              <mo>×</mo>
                              <mn>{h.toFixed(2)}</mn>
                              <mo>−</mo>
                              <mn>{dw.toFixed(2)}</mn>
                              <mo>)</mo>
                              <mn>{dw.toFixed(2)}</mn>
                            </mrow>
                            <msup>
                              <mn>{h.toFixed(2)}</mn>
                              <mn>2</mn>
                            </msup>
                          </mfrac>
                          <mo>]</mo>
                          <mo>+</mo>
                          <mn>{gammaEffectivePrime.toFixed(2)}</mn>
                          <mo>[</mo>
                          <mfrac>
                            <msup>
                              <mrow>
                                <mo>(</mo>
                                <mn>{h.toFixed(2)}</mn>
                                <mo>−</mo>
                                <mn>{dw.toFixed(2)}</mn>
                                <mo>)</mo>
                              </mrow>
                              <mn>2</mn>
                            </msup>
                            <msup>
                              <mn>{h.toFixed(2)}</mn>
                              <mn>2</mn>
                            </msup>
                          </mfrac>
                          <mo>]</mo>
                        </math>
                      </Typography>

                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        &gamma;<sub>e</sub> = {gammaEffective.toFixed(2)} kN/m³
                      </Typography>
                    </>
                  )}
                  {appliedCase.caseId === 3 && (
                    <>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        It is known from the inputs that GWT = {gwt}m &gt; D<sub>f</sub> = {df}m. So, we should also check the
                        failure wedge depth (H) to find if the case is 2 or 3.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        H = D<sub>f</sub> + 0.5Btan(45 + &phi;/2)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        H = {df} + (0.5)({fndWidth})tan(45 + {phi}/2)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        H = {h.toFixed(2)}m
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        {' '}
                        Since GWT = {gwt}m &gt; H = {h.toFixed(2)}m, use <b>Case 3</b> formulation to calculate γ<sub>e</sub>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        &gamma;<sub>e</sub> = &gamma; = {gammaEffective.toFixed(2)} kN/m³
                      </Typography>
                    </>
                  )}
                </Box>
              </Collapse>
            )}
          </Alert>
        </Alert>
      </Box>
    );
  };

  const BearingCapacityInfoBox = () => {
    const [showUltimateBearingCapacityDetails, setShowUltimateBearingCapacityDetails] = useState(false);
    const [showStaticAllowableBearingCapacityDetails, setShowStaticAllowableBearingCapacityDetails] = useState(false);
    const [showSeismicAllowableBearingCapacityDetails, setShowSeismicAllowableBearingCapacityDetails] = useState(false);

    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Bearing Capacity
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
            The bearing capacity represents the maximum pressure that the foundation soil can sustain without failure. It is first
            calculated as the ultimate bearing capacity (q<sub>ult</sub>), which combines the effects of cohesion, surcharge, and
            soil unit weight, including shape factors that adjust for foundation geometry.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
            The allowable bearing capacity (q<sub>all</sub>) is then obtained by dividing q<sub>ult</sub> by an appropriate factor
            of safety (F.S.), typically between 2.5 and 3 for static case and between 1.00 to 1.10 for seismic case. This
            represents the safe working load that the soil can carry without excessive settlement or failure.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Ultimate Bearing Capacity */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowUltimateBearingCapacityDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showUltimateBearingCapacityDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  q<sub>ult</sub>
                </b>{' '}
                (ultimate bearing capacity): {qUlt || qUlt === 0 ? round(qUlt, 0) : '—'} kPa
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showUltimateBearingCapacityDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    Since we have {isThetaZero ? 'no load inclination (θ = 0°)' : `a load inclination of θ = ${round(theta, 2)}°`}
                    , the following bearing capacity equation should be used:
                  </Typography>

                  {isThetaZero ? (
                    <Typography color="text.secondary" sx={{ mt: 1.5, mb: 1, lineHeight: 1.6, fontWeight: 600 }}>
                      <math display="block">
                        <mrow>
                          <mspace width="0.5em" />
                          <msub>
                            <mi>q</mi>
                            <mi>ult</mi>
                          </msub>
                          <mo>=</mo>
                          <mi>c</mi>
                          <msub>
                            <mi>N</mi>
                            <mi>c</mi>
                          </msub>
                          <msub>
                            <mi>s</mi>
                            <mi>c</mi>
                          </msub>
                          <msub>
                            <mi>d</mi>
                            <mi>c</mi>
                          </msub>
                          <mo>+</mo>
                          <mover>
                            <mi>q</mi>
                            <mo>¯</mo>
                          </mover>
                          <msub>
                            <mi>N</mi>
                            <mi>q</mi>
                          </msub>
                          <msub>
                            <mi>s</mi>
                            <mi>q</mi>
                          </msub>
                          <msub>
                            <mi>d</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>+</mo>
                          <mn>0.5</mn>
                          <msub>
                            <mi>γ</mi>
                            <mo>e</mo>
                          </msub>
                          <mi>B</mi>
                          <msub>
                            <mi>N</mi>
                            <mi>γ</mi>
                          </msub>
                          <msub>
                            <mi>s</mi>
                            <mi>γ</mi>
                          </msub>
                          <msub>
                            <mi>d</mi>
                            <mi>γ</mi>
                          </msub>
                        </mrow>
                      </math>
                    </Typography>
                  ) : (
                    <Typography color="text.secondary" sx={{ mt: 0.5, mb: 1.5, lineHeight: 1.6, fontWeight: 600 }}>
                      <math display="block">
                        <mrow>
                          <mspace width="0.5em" />
                          <msub>
                            <mi>q</mi>
                            <mi>ult</mi>
                          </msub>
                          <mo>=</mo>
                          <mi>c</mi>
                          <msub>
                            <mi>N</mi>
                            <mi>c</mi>
                          </msub>
                          <msub>
                            <mi>d</mi>
                            <mi>c</mi>
                          </msub>
                          <msub>
                            <mi>i</mi>
                            <mi>c</mi>
                          </msub>
                          <mo>+</mo>
                          <mover>
                            <mi>q</mi>
                            <mo>¯</mo>
                          </mover>
                          <msub>
                            <mi>N</mi>
                            <mi>q</mi>
                          </msub>
                          <msub>
                            <mi>d</mi>
                            <mi>q</mi>
                          </msub>
                          <msub>
                            <mi>i</mi>
                            <mi>q</mi>
                          </msub>
                          <mo>+</mo>
                          <mn>0.5</mn>
                          <msub>
                            <mi>γ</mi>
                            <mo>e</mo>
                          </msub>
                          <mi>B</mi>
                          <msub>
                            <mi>N</mi>
                            <mi>γ</mi>
                          </msub>
                          <msub>
                            <mi>d</mi>
                            <mi>γ</mi>
                          </msub>
                          <msub>
                            <mi>i</mi>
                            <mi>γ</mi>
                          </msub>
                        </mrow>
                      </math>
                    </Typography>
                  )}
                  {isThetaZero ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      mb={1}
                      mt={2}
                      sx={{
                        textAlign: 'center',
                        fontStyle: 'italic',
                        fontSize: '14px',
                        letterSpacing: '0.4px',
                      }}
                    >
                      q<sub>ult</sub> =({cohesion || cohesion === 0 ? round(cohesion, 2) : '—'})×(
                      {nc || nc === 0 ? round(nc, 2) : '—'})×({sc || sc === 0 ? round(sc, 2) : '—'})×(
                      {dc || dc === 0 ? round(dc, 2) : '—'}) + (
                      {effectiveStress || effectiveStress === 0 ? round(effectiveStress, 2) : '—'})×(
                      {nq || nq === 0 ? round(nq, 2) : '—'})×({sq || sq === 0 ? round(sq, 2) : '—'})×(
                      {dq || dq === 0 ? round(dq, 2) : '—'}) + 0.50×(
                      {gammaEffective || gammaEffective === 0 ? round(gammaEffective, 2) : '—'})×(
                      {fndWidth || fndWidth === 0 ? round(fndWidth, 2) : '—'})×(
                      {ngamma || ngamma === 0 ? round(ngamma, 2) : '—'})×({sgamma || sgamma === 0 ? round(sgamma, 2) : '—'})×(
                      {dgamma || dgamma === 0 ? round(dgamma, 2) : '—'})
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      mb={1}
                      mt={2}
                      sx={{
                        textAlign: 'center',
                        fontStyle: 'italic',
                        fontSize: '14px',
                        letterSpacing: '0.4px',
                      }}
                    >
                      q<sub>ult</sub> =({cohesion || cohesion === 0 ? round(cohesion, 2) : '—'})×(
                      {nc || nc === 0 ? round(nc, 2) : '—'})×({dc || dc === 0 ? round(dc, 2) : '—'})×(
                      {ic || ic === 0 ? round(ic, 2) : '—'}) + (
                      {effectiveStress || effectiveStress === 0 ? round(effectiveStress, 2) : '—'})×(
                      {nq || nq === 0 ? round(nq, 2) : '—'})×({dq || dq === 0 ? round(dq, 2) : '—'})×(
                      {iq || iq === 0 ? round(iq, 2) : '—'}) + 0.50×(
                      {gammaEffective || gammaEffective === 0 ? round(gammaEffective, 2) : '—'})×(
                      {fndWidth || fndWidth === 0 ? round(fndWidth, 2) : '—'})×(
                      {ngamma || ngamma === 0 ? round(ngamma, 2) : '—'})×({dgamma || dgamma === 0 ? round(dgamma, 2) : '—'})×(
                      {igamma || igamma === 0 ? round(igamma, 2) : '—'})
                    </Typography>
                  )}

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mi>ult</mi>
                      </msub>
                      <mtext> = </mtext>
                      <mn>{qUlt || qUlt === 0 ? round(qUlt, 0) : '—'} kPa</mn>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* Allowabile Capacity - Static */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowStaticAllowableBearingCapacityDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showStaticAllowableBearingCapacityDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  q<sub>all-st</sub>
                </b>{' '}
                (allowable bearing capacity - static case): {qallSt ? qallSt.toFixed(0) : '—'} kPa
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showStaticAllowableBearingCapacityDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={2} mt={2}>
                    We can now determine the allowable bearing capacity for the static case (q<sub>all-st</sub>)by applying the
                    factor of safety you provided (FS<sub>St</sub> = {FsSt.toFixed(2)}) to the previously computed ultimate
                    bearing capacity. This ensures that the working load on the foundation remains within safe limits under
                    long-term static conditions.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mrow>
                          <mi>all</mi>
                          <mo>-</mo>
                          <mi>st</mi>
                        </mrow>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <msub>
                          <mi>q</mi>
                          <mi>ult</mi>
                        </msub>
                        <msub>
                          <mi>FS</mi>
                          <mrow>
                            <mi>st</mi>
                          </mrow>
                        </msub>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mrow>
                          <mi>all</mi>
                          <mo>-</mo>
                          <mi>st</mi>
                        </mrow>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <mn>{qUlt != null ? qUlt.toFixed(0) : '—'}</mn>
                        <mn>{FsSt != null ? FsSt.toFixed(2) : '—'}</mn>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    From this, the allowable bearing capacity for the static case is:
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mrow>
                          <mi>all</mi>
                          <mo>-</mo>
                          <mi>st</mi>
                        </mrow>
                      </msub>
                      <mo>=</mo>
                      <mn>{qallSt != null ? qallSt.toFixed(0) : '—'}</mn>
                      <mo> </mo>
                      <mi>kPa</mi>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* Allowabile Capacity - Seismic */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowSeismicAllowableBearingCapacityDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showSeismicAllowableBearingCapacityDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  q<sub>all-ss</sub>
                </b>{' '}
                (allowable bearing capacity - seismic case): {qallSs ? qallSs.toFixed(0) : '—'} kPa
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showSeismicAllowableBearingCapacityDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={2} mt={2}>
                    We can now determine the allowable bearing capacity for the seismic case (q<sub>all-ss</sub>) by applying the
                    seismic factor of safety you provided (Fs<sub>ss</sub> = {FsSs.toFixed(2)}) to the previously computed
                    ultimate bearing capacity. This represents the reduced safe bearing pressure under earthquake loading,
                    ensuring the foundation remains stable during seismic events.
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mrow>
                          <mi>all</mi>
                          <mo>-</mo>
                          <mi>ss</mi>
                        </mrow>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <msub>
                          <mi>q</mi>
                          <mi>ult</mi>
                        </msub>
                        <msub>
                          <mi>FS</mi>
                          <mrow>
                            <mi>ss</mi>
                          </mrow>
                        </msub>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mrow>
                          <mi>all</mi>
                          <mo>-</mo>
                          <mi>ss</mi>
                        </mrow>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <mn>{qUlt != null ? qUlt.toFixed(0) : '—'}</mn>
                        <mn>{FsSs != null ? FsSs.toFixed(2) : '—'}</mn>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    From this, the allowable bearing capacity for the seismic case is:
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mrow>
                          <mi>all</mi>
                          <mo>-</mo>
                          <mi>ss</mi>
                        </mrow>
                      </msub>
                      <mo>=</mo>
                      <mn>{qallSs != null ? qallSs.toFixed(0) : '—'}</mn>
                      <mo> </mo>
                      <mi>kPa</mi>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };

  const BearingCapacityVsLoadsInfoBox = () => {
    const [showBearingCapacityVsLoadsDetailsStatic, setShowBearingCapacityVsLoadsDetailsStatic] = useState(false);
    const [showBearingCapacityVsLoadsDetailsSeismic, setShowBearingCapacityVsLoadsDetailsSeismic] = useState(false);

    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Allowable Bearing Capacities Vs Loads
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
            Finally, the computed allowable bearing capacity values will be compared with the design loads you provided. This
            comparison ensures that the applied foundation pressures remain below the allowable limits, confirming that the soil
            can safely support the imposed loads without exceeding its bearing capacity in static and seismic cases.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Static Case */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowBearingCapacityVsLoadsDetailsStatic((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showBearingCapacityVsLoadsDetailsStatic ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>Static Case:</b>
                {isQallStEnough ? (
                  <>
                    <VerifiedIcon sx={{ color: 'success.main', fontSize: 22 }} />{' '}
                    <Typography variant="body2" color="success.main" fontWeight={600}>
                      OK!
                    </Typography>
                  </>
                ) : (
                  <>
                    <CancelIcon sx={{ color: 'error.main', fontSize: 22 }} />{' '}
                    <Typography variant="body2" color="error.main" fontWeight={600}>
                      Not OK!
                    </Typography>
                  </>
                )}
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showBearingCapacityVsLoadsDetailsStatic} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    We can now compare the static allowable bearing capacity (q<sub>all-st</sub>) with the static foundation
                    pressure you provided to assess whether the soil can safely support the applied load under static conditions.
                  </Typography>

                  {isQallStEnough ? (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} textAlign={'center'}>
                        q<sub>all-st</sub> = {qallSt.toFixed(0)} kPa &gt; FSt = {FSt.toFixed(0)} kPa
                      </Typography>
                      <Typography variant="body2" color="success.main" fontWeight={600}>
                        The allowable bearing capacity exceeds the applied static foundation pressure.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} textAlign={'center'}>
                        q<sub>all-st</sub> = {qallSt.toFixed(0)} kPa &lt; FSt = {FSt.toFixed(0)} kPa
                      </Typography>

                      <Typography variant="body2" color="error.main" fontWeight={600}>
                        The allowable bearing capacity is less than the applied static foundation pressure.
                      </Typography>
                    </>
                  )}
                </Collapse>
              )}
            </Alert>
            {/* Seismic Case */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowBearingCapacityVsLoadsDetailsSeismic((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showBearingCapacityVsLoadsDetailsSeismic ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>Seismic Case:</b>
                {isQallSsEnough ? (
                  <>
                    <VerifiedIcon sx={{ color: 'success.main', fontSize: 22 }} />{' '}
                    <Typography variant="body2" color="success.main" fontWeight={600}>
                      OK!
                    </Typography>
                  </>
                ) : (
                  <>
                    <CancelIcon sx={{ color: 'error.main', fontSize: 22 }} />{' '}
                    <Typography variant="body2" color="error.main" fontWeight={600}>
                      Not OK!
                    </Typography>
                  </>
                )}
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showBearingCapacityVsLoadsDetailsSeismic} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    We can now compare the seismic allowable bearing capacity (q<sub>all-ss</sub>) with the seismic foundation
                    pressure you provided to assess whether the soil can safely support the applied load under seismic conditions.
                  </Typography>

                  {isQallSsEnough ? (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} textAlign={'center'}>
                        q<sub>all-ss</sub> = {qallSs.toFixed(0)} kPa &gt; FSs = {FSs.toFixed(0)} kPa
                      </Typography>
                      <Typography variant="body2" color="success.main" fontWeight={600}>
                        The allowable bearing capacity exceeds the applied seismic foundation pressure.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} textAlign={'center'}>
                        q<sub>all-ss</sub> = {qallSs.toFixed(0)} kPa &lt; FSs = {FSs.toFixed(0)} kPa
                      </Typography>

                      <Typography variant="body2" color="error.main" fontWeight={600}>
                        The allowable bearing capacity is less than the applied seismic foundation pressure.
                      </Typography>
                    </>
                  )}
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };

  return (
    <section className="output meyerhofOutput">
      <MeyerhofInfoBox />
      <BearingCapacityFactorsInfoBox />
      {isThetaZero ? <ShapeFactorsInfoBox /> : <InclinationFactorsInfoBox />}
      <DepthFactorsInfoBox />

      <WaterTableGammaEInfoBox />
      <BearingCapacityInfoBox />
      <BearingCapacityVsLoadsInfoBox />
    </section>
  );
};

export default MeyerhofResult;
